/**
 * Opt-in "Deep relationship discovery" for file / folder extracts (#812 step 3).
 *
 * Name evidence finds the relationships whose column names reveal their parent. This option also compares VALUES across
 * tables for the rest (extracts only: files and folders, never a live database). It is off by default because it adds time and produces suggestions, not facts. The copy below is
 * the explanation and pros/cons shown to the user; keep it in step with what the backend actually does
 * (ztd_app/tools/profiler/relationship_discovery.py).
 */

export const DEEP_RELATIONSHIP_LABEL = "Find relationships by comparing values";

export const DEEP_RELATIONSHIP_SUMMARY =
  "Checks whether the values in one table's columns match another table's key, even when the column names don't say so (for example cust_no matching customers.customer_id). Matches are listed separately as Possible relationships and never change the quality score. Best for files with cryptic or inconsistent column names.";

export const DEEP_RELATIONSHIP_PROS = [
  "Finds links that name matching misses, such as cust_no pointing at customers.customer_id.",
  "Shows rows whose reference has no match in the other table, even when names give nothing away.",
];

export const DEEP_RELATIONSHIP_CONS = [
  "Takes longer: a few seconds for small files, and it stops at a time limit on large ones.",
  "Only about 1 in 3 suggestions is a real relationship in our tests, mostly because sequential ID columns look alike. Treat each one as a lead to check.",
  "On files with clear column names it mostly adds noise, because name matching already finds those links.",
];

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function DeepRelationshipOption({ checked, onChange, disabled }: Props) {
  return (
    <div
      className="glass-surface rounded-xl p-4"
      data-testid="deep-relationship-option"
    >
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 rounded border-[var(--border)] bg-transparent accent-[var(--primary)]"
          data-testid="deep-relationship-checkbox"
        />
        <span className="flex-1">
          <span className="flex items-center gap-2 text-sm text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors">
            {DEEP_RELATIONSHIP_LABEL}
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/5 text-[var(--muted-foreground)]">
              Optional
            </span>
          </span>
          <span className="block text-[11px] text-[var(--muted-foreground)] mt-0.5">
            {DEEP_RELATIONSHIP_SUMMARY}
          </span>
        </span>
      </label>
      <details className="mt-2 ml-7 text-[11px] text-[var(--muted-foreground)]">
        <summary className="cursor-pointer hover:text-[var(--foreground)]">
          What's this? Pros and cons
        </summary>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div>
            <p className="font-semibold text-[var(--foreground)] mb-1">Pros</p>
            <ul className="list-disc pl-4 space-y-1">
              {DEEP_RELATIONSHIP_PROS.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-semibold text-[var(--foreground)] mb-1">Cons</p>
            <ul className="list-disc pl-4 space-y-1">
              {DEEP_RELATIONSHIP_CONS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      </details>
    </div>
  );
}
